import apiClient from '@/api/client';
import { getActiveHospitalId, getActiveClinicId } from '@/constants';
import { utcToKolkataParts, formatDateTime, formatDate } from '@/utils/dateTime';

const resolveDoctorName = (rawName, firstName, lastName) => {
  const clean = (rawName || '').replace(/^Dr\.\s*/i, '').trim();
  if (clean && clean !== 'Consultant' && clean !== 'Doctor') {
    return `Dr. ${clean}`;
  }
  if (firstName || lastName) {
    return `Dr. ${firstName || ''} ${lastName || ''}`.trim();
  }
  return '';
};

const resolvePatientName = (rawName, firstName, lastName) => {
  const clean = (rawName || '').trim();
  if (clean && clean !== 'Patient' && clean !== 'Unknown') {
    return clean;
  }
  if (firstName || lastName) {
    return `${firstName || ''} ${lastName || ''}`.trim();
  }
  return '';
};

const transformConsultation = (cons) => {
  if (!cons) return null;
  const rawUtcTimestamp = cons.consultationDate || cons.createdAt || cons.date;
  const parts = utcToKolkataParts(rawUtcTimestamp);

  const rawPatientName = cons.patientName || cons.patient?.name || '';
  const patientFirstName = cons.patient?.firstName || cons.patientFirstName || rawPatientName.split(' ')[0] || '';
  const patientLastName = cons.patient?.lastName || cons.patientLastName || (rawPatientName.split(' ').length > 1 ? rawPatientName.split(' ').slice(1).join(' ') : '');
  const patientFullName = resolvePatientName(rawPatientName, patientFirstName, patientLastName);

  const rawDoctorName = cons.doctorName || cons.doctor?.name || '';
  const cleanDoc = (rawDoctorName || '').replace(/^Dr\.\s*/i, '').trim();
  const doctorFirstName = cons.doctor?.firstName || cons.doctorFirstName || cleanDoc.split(' ')[0] || '';
  const doctorLastName = cons.doctor?.lastName || cons.doctorLastName || (cleanDoc.split(' ').length > 1 ? cleanDoc.split(' ').slice(1).join(' ') : '');
  const doctorFullName = resolveDoctorName(rawDoctorName, doctorFirstName, doctorLastName);

  return {
    ...cons,
    date: parts ? parts.dateString : (cons.date || ''),
    time: parts ? parts.time24String : '',
    formattedDate: parts ? formatDate(rawUtcTimestamp) : (cons.date || ''),
    formattedDateTime: parts ? formatDateTime(rawUtcTimestamp) : (cons.date || ''),
    patientName: patientFullName,
    doctorName: doctorFullName,
    patient: {
      id: cons.patientId || cons.patient?.id,
      name: patientFullName,
      firstName: patientFirstName,
      lastName: patientLastName,
      patientCode: cons.patientCode || cons.patient?.patientCode || '',
      phone: cons.patientPhone || cons.patient?.phone || cons.phone || '',
      age: cons.patientAge ?? cons.patient?.age ?? cons.age,
      dateOfBirth: cons.patientDateOfBirth || cons.patient?.dateOfBirth || cons.dateOfBirth,
      gender: cons.patientGender || cons.patient?.gender || cons.gender || '',
      ...(cons.patient || {}),
    },
    doctor: {
      id: cons.doctorId || cons.doctor?.id,
      name: doctorFullName,
      firstName: doctorFirstName,
      lastName: doctorLastName,
      specialization: cons.doctorSpecialization || cons.doctor?.specialization || cons.specialization || '',
      ...(cons.doctor || {}),
    },
    clinic: cons.clinic || {
      id: cons.clinicId,
      name: cons.clinicName || 'Main OPD Clinic',
    },
  };
};

const transformQueueItem = (a) => {
  if (!a) return null;
  const rawUtc = a.appointmentDateTime || a.dateTime || a.date;
  const parts = utcToKolkataParts(rawUtc);

  const rawPatientName = a.patientName || a.patient?.name || '';
  const patientFirstName = a.patient?.firstName || a.patientFirstName || rawPatientName.split(' ')[0] || '';
  const patientLastName = a.patient?.lastName || a.patientLastName || (rawPatientName.split(' ').length > 1 ? rawPatientName.split(' ').slice(1).join(' ') : '');
  const patientFullName = resolvePatientName(rawPatientName, patientFirstName, patientLastName);

  const rawDoctorName = a.doctorName || a.doctor?.name || '';
  const cleanDoc = (rawDoctorName || '').replace(/^Dr\.\s*/i, '').trim();
  const doctorFirstName = a.doctor?.firstName || a.doctorFirstName || cleanDoc.split(' ')[0] || '';
  const doctorLastName = a.doctor?.lastName || a.doctorLastName || (cleanDoc.split(' ').length > 1 ? cleanDoc.split(' ').slice(1).join(' ') : '');
  const doctorFullName = resolveDoctorName(rawDoctorName, doctorFirstName, doctorLastName);
  const doctorSpec = a.doctorSpecialization || a.doctor?.specialization || a.specialization || '';

  return {
    ...a,
    date: parts ? parts.dateString : a.date,
    time: parts ? parts.time24String : a.time,
    timeFormatted: parts ? parts.time12String : a.time,
    formattedDateTime: parts ? formatDateTime(rawUtc) : `${a.date} ${a.time}`,
    patientName: patientFullName,
    doctorName: doctorFullName,
    patient: {
      id: a.patientId || a.patient?.id,
      name: patientFullName,
      firstName: patientFirstName,
      lastName: patientLastName,
      patientCode: a.patientCode || a.patient?.patientCode || '',
      phone: a.patientPhone || a.patient?.phone || a.phone || '',
      age: a.patientAge ?? a.patient?.age ?? a.age,
      dateOfBirth: a.patientDateOfBirth || a.patient?.dateOfBirth || a.dateOfBirth,
      gender: a.patientGender || a.patient?.gender || a.gender || '',
      ...(a.patient || {}),
    },
    doctor: {
      id: a.doctorId || a.doctor?.id,
      name: doctorFullName,
      firstName: doctorFirstName,
      lastName: doctorLastName,
      specialization: doctorSpec,
      ...(a.doctor || {}),
    },
  };
};

export const consultationService = {
  /**
   * GET /pms/hospital/{hospitalId}/clinic/{clinicId}/consultations
   */
  getAll: async (params = {}, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const clinicId = getActiveClinicId(params.clinicId);

    let endpoint = `/hospital/${activeHospId}/clinic/${clinicId}/consultations`;
    if (params.patientId) {
      endpoint = `/hospital/${activeHospId}/clinic/${clinicId}/consultations/patient/${params.patientId}`;
    }

    const res = await apiClient.get(endpoint, {
      params: {
        page: (params.page || 1) - 1,
        size: params.limit || 20,
      },
    });

    const items = (res.content || res.data || []).map(transformConsultation);
    return {
      data: items,
      total: res.totalElements !== undefined ? res.totalElements : items.length,
      page: (res.pageNumber !== undefined ? res.pageNumber : 0) + 1,
      totalPages: res.totalPages || 1,
    };
  },

  /**
   * GET /pms/hospital/{hospitalId}/clinic/{clinicId}/consultations/queue
   */
  getQueue: async (clinicId, doctorId = null, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const res = await apiClient.get(`/hospital/${activeHospId}/clinic/${activeClinicId}/consultations/queue`, {
      params: { doctorId: doctorId !== 'all' ? doctorId : undefined },
    });
    const data = res.data || res;
    return {
      waiting: (data.waiting || []).map(transformQueueItem),
      inProgress: (data.inProgress || []).map(transformQueueItem),
      completed: (data.completed || []).map(transformQueueItem),
      totalToday: data.totalToday || 0,
    };
  },

  /**
   * GET /pms/hospital/{hospitalId}/clinic/{clinicId}/consultations/{consultationId}
   */
  getById: async (id, clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const res = await apiClient.get(`/hospital/${activeHospId}/clinic/${activeClinicId}/consultations/${id}`);
    return transformConsultation(res.data || res);
  },

  /**
   * Helper to normalize clinical payload
   */
  normalizePayload: (data) => {
    return {
      appointmentId: data.appointmentId || null,
      patientId: data.patientId,
      doctorId: data.doctorId,
      clinicId: data.clinicId,
      date: data.date,
      status: data.status || (data.isDraft ? 'DRAFT' : 'COMPLETED'),
      chiefComplaints: data.chiefComplaints || data.symptoms || '',
      symptoms: data.symptoms || '',
      examination: data.examination || '',
      diagnosis: data.diagnosis || '',
      bloodPressure: data.bloodPressure || data.vitals?.bloodPressure,
      heartRate: data.heartRate || data.vitals?.heartRate,
      temperature: data.temperature || data.vitals?.temperature,
      weight: data.weight || data.vitals?.weight,
      height: data.height || data.vitals?.height,
      vitals: data.vitals || {
        bloodPressure: data.bloodPressure,
        heartRate: data.heartRate,
        temperature: data.temperature,
        weight: data.weight,
        height: data.height,
      },
      doctorNotes: data.doctorNotes || data.notes || '',
      notes: data.notes || data.doctorNotes || '',
      followUpRequired: !!data.followUpRequired,
      followUpDate: data.followUpDate || null,
      prescriptions: (data.prescriptions || data.prescription || []).map((p, idx) => ({
        id: p.id || `pi-${Date.now()}-${idx}`,
        medicineName: p.medicineName || p.medication || p.medicine || '',
        amount: p.amount || p.dosage || '1 tablet',
        dosage: p.dosage || p.amount || '1 tablet',
        quantity: Number(p.quantity) || 10,
        frequency: p.frequency || 'Twice daily (BD)',
        duration: p.duration || '5 days',
        instructions: p.instructions || p.notes || '',
        notes: p.notes || p.instructions || '',
        displayOrder: p.displayOrder || idx + 1,
      })),
      completedAt: data.status === 'COMPLETED' || !data.isDraft ? (data.completedAt || new Date().toISOString()) : null,
    };
  },

  /**
   * POST /pms/hospital/{hospitalId}/clinic/{clinicId}/consultations
   */
  create: async function (data, hospitalId) {
    const activeHospId = getActiveHospitalId(hospitalId);
    const clinicId = getActiveClinicId(data.clinicId);
    const payload = this.normalizePayload(data);

    try {
      const res = await apiClient.post(
        `/hospital/${activeHospId}/clinic/${clinicId}/consultations`,
        payload
      );
      return transformConsultation(res.data || res);
    } catch (err) {
      // Local storage resilience for draft / offline workflow
      const localId = `cons-${Date.now()}`;
      const savedCons = {
        ...payload,
        id: localId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const stored = JSON.parse(localStorage.getItem('pulseflow_local_consultations') || '[]');
      localStorage.setItem('pulseflow_local_consultations', JSON.stringify([savedCons, ...stored]));
      return transformConsultation(savedCons);
    }
  },

  /**
   * PUT /pms/hospital/{hospitalId}/clinic/{clinicId}/consultations/{consultationId}
   */
  update: async function (id, data, hospitalId) {
    const activeHospId = getActiveHospitalId(hospitalId);
    const clinicId = getActiveClinicId(data.clinicId);
    const payload = this.normalizePayload(data);

    try {
      const res = await apiClient.put(
        `/hospital/${activeHospId}/clinic/${clinicId}/consultations/${id}`,
        payload
      );
      return transformConsultation(res.data || res);
    } catch (err) {
      const stored = JSON.parse(localStorage.getItem('pulseflow_local_consultations') || '[]');
      const idx = stored.findIndex((c) => c.id === id);
      const updated = { ...payload, id, updatedAt: new Date().toISOString() };
      if (idx !== -1) {
        stored[idx] = updated;
      } else {
        stored.unshift(updated);
      }
      localStorage.setItem('pulseflow_local_consultations', JSON.stringify(stored));
      return transformConsultation(updated);
    }
  },

  /**
   * Save consultation as DRAFT
   */
  saveDraft: async function (id, data, hospitalId) {
    const draftPayload = { ...data, status: 'DRAFT', isDraft: true };
    if (id && id !== 'new') {
      return this.update(id, draftPayload, hospitalId);
    }
    return this.create(draftPayload, hospitalId);
  },

  /**
   * Resume consultation from DRAFT to IN_PROGRESS
   */
  resume: async function (id, clinicId, hospitalId) {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);

    try {
      const res = await apiClient.post(
        `/hospital/${activeHospId}/clinic/${activeClinicId}/consultations/${id}/resume`
      );
      return transformConsultation(res.data || res);
    } catch (err) {
      const cons = await this.getById(id, clinicId, hospitalId);
      if (cons) {
        cons.status = 'IN_PROGRESS';
        return this.update(id, cons, hospitalId);
      }
      return null;
    }
  },

  /**
   * Complete consultation: validates & transitions to COMPLETED
   */
  complete: async function (id, data, hospitalId) {
    const activeHospId = getActiveHospitalId(hospitalId);
    const clinicId = getActiveClinicId(data.clinicId);
    const completedPayload = {
      ...data,
      status: 'COMPLETED',
      isDraft: false,
      completedAt: new Date().toISOString(),
    };

    try {
      const res = await apiClient.post(
        `/hospital/${activeHospId}/clinic/${clinicId}/consultations/${id}/complete`,
        completedPayload
      );
      return transformConsultation(res.data || res);
    } catch (err) {
      if (id && id !== 'new') {
        return this.update(id, completedPayload, hospitalId);
      }
      return this.create(completedPayload, hospitalId);
    }
  },
};

export default consultationService;
